/**
 * Auth Store (Zustand)
 * สโตร์สำหรับจัดเก็บสถานะการยืนยันตัวตน
 *
 * Security: Access token is stored in MEMORY ONLY (never localStorage).
 * ความปลอดภัย: Access token จัดเก็บในหน่วยความจำเท่านั้น (ไม่เก็บใน localStorage)
 */

import { create } from 'zustand'
import type { User } from './types'

interface AuthState {
  // State
  accessToken: string | null // Memory only — never persisted
  user: User | null
  isLoading: boolean

  // Actions
  setAccessToken: (token: string | null) => void
  setUser: (user: User | null) => void
  setLoading: (loading: boolean) => void
  login: (accessToken: string, user: User) => void
  logout: () => void
}

// Demo user for development and tests
export const DEMO_USER: User = {
  id: '1',
  email: 'auditor@example.com',
  firstName: 'ภาณุพงศ์',
  lastName: 'พารณดี',
  fullName: 'ภาณุพงศ์ พารณดี',
  avatarUrl: null,
  role: 'auditor',
  domainId: 'domain-1',
  permissions: ['companies:read', 'companies:create', 'companies:update', 'companies:delete'],
  isActive: true,
  lastLoginAt: new Date().toISOString(),
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
}

export const useAuthStore = create<AuthState>((set) => ({
  // Default state: Unauthenticated — AuthProvider will resolve via refreshToken
  accessToken: null,
  user: null,
  isLoading: true,

  setAccessToken: (token) => {
    set({ accessToken: token })
  },
  setUser: (user) => {
    set({ user })
  },
  setLoading: (loading) => {
    set({ isLoading: loading })
  },

  login: (accessToken, user) => {
    set({
      accessToken,
      user,
      isLoading: false,
    })
  },

  logout: () => {
    set({
      accessToken: null,
      user: null,
      isLoading: false,
    })
  },
}))

/**
 * Derived selector: Check if user is authenticated
 * ตัวเลือก: ตรวจสอบว่าผู้ใช้ยืนยันตัวตนแล้วหรือไม่
 */
export const useIsAuthenticated = () => useAuthStore((s) => !!s.accessToken && !!s.user)
